"use client";

import { Check, Star, Zap } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface PricingFeature {
	text: string;
	included?: boolean;
}

interface PricingPlan {
	id: string;
	name: string;
	description: string;
	monthlyPrice: number;
	yearlyPrice: number;
	features: PricingFeature[];
	button: {
		text: string;
		url: string;
		variant?: "default" | "outline" | "secondary";
	};
	popular?: boolean;
	icon?: React.ReactNode;
	discount?: number;
}

interface PricingPlanProps {
	heading?: string;
	description?: string;
	plans?: PricingPlan[];
	className?: string;
	showYearlyDiscount?: boolean;
}

const PricingPlan = ({
	heading = "Choose Your Plan",
	description = "Select the perfect plan for your needs. Upgrade or downgrade at any time.",
	showYearlyDiscount = true,
	plans = [
		{
			id: "starter",
			name: "Starter",
			description: "Perfect for individuals and small projects",
			monthlyPrice: 9,
			yearlyPrice: 90,
			features: [{ text: "Up to 3 projects" }, { text: "Basic analytics" }, { text: "Email support" }, { text: "API access" }],
			button: {
				text: "Start Free Trial",
				url: "#starter",
				variant: "outline",
			},
		},
	],
	className,
}: PricingPlanProps) => {
	const [isYearly, setIsYearly] = useState(false);

	const formatPrice = (price: number) => {
		return new Intl.NumberFormat("en-GB", {
			style: "currency",
			currency: "GBP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(price);
	};

	return (
		<section className={cn("py-24 lg:py-32", className)}>
			<div className="container">
				<div className="mx-auto flex flex-col items-center gap-8 text-center">
					<div className="space-y-4">
						<h2 className="text-4xl font-semibold tracking-tight text-pretty lg:text-6xl">{heading}</h2>
						<p className="text-muted-foreground text-lg lg:text-xl">{description}</p>
					</div>

					<div className="flex items-center justify-center gap-4">
						<span className={cn("text-sm font-medium transition-colors", !isYearly ? "text-foreground" : "text-muted-foreground")}>Monthly</span>
						<button type="button" role="switch" aria-checked={isYearly} onClick={() => setIsYearly(!isYearly)} className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", isYearly ? "bg-primary" : "bg-input")}>
							<span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", isYearly ? "translate-x-6" : "translate-x-1")} />
						</button>
						<span className={cn("text-sm font-medium transition-colors", isYearly ? "text-foreground" : "text-muted-foreground")}>Yearly</span>
						{showYearlyDiscount && <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">Save 17%</span>}
					</div>

					<div className="w-full max-w-6xl mx-auto">
						<div className={cn("grid gap-6 justify-items-center", plans.length === 1 && "lg:grid-cols-1 max-w-md mx-auto", plans.length === 2 && "lg:grid-cols-2 max-w-4xl mx-auto", plans.length >= 3 && "lg:grid-cols-3")}>
							{plans.map((plan) => (
								<Card key={plan.id} className={cn("relative flex w-full flex-col justify-between transition-all hover:shadow-lg", plan.popular && "border-primary shadow-md scale-105", !plan.popular && "hover:scale-[1.02]")}>
									{plan.popular && (
										<div className="absolute -top-3 left-1/2 -translate-x-1/2">
											<div className="flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
												<Star className="h-3 w-3 fill-current" />
												Most Popular
											</div>
										</div>
									)}

									<CardHeader className="text-center pb-4">
										<div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
											{plan.id === "free" && <Check className="h-6 w-6 text-primary" />}
											{plan.id === "pro" && <Star className="h-6 w-6 text-primary" />}
											{plan.id === "starter" && <Zap className="h-6 w-6 text-primary" />}
											{plan.id === "professional" && <Star className="h-6 w-6 text-primary" />}
											{plan.id === "enterprise" && <Check className="h-6 w-6 text-primary" />}
											{!plan.id || (!["free", "pro", "starter", "professional", "enterprise"].includes(plan.id) && <Star className="h-6 w-6 text-primary" />)}
										</div>
										<CardTitle className="text-xl">{plan.name}</CardTitle>
										<p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
										<div className="mt-4 flex items-baseline justify-center gap-1">
											<span className="text-4xl font-bold">{formatPrice(isYearly ? plan.yearlyPrice : plan.monthlyPrice)}</span>
											<span className="text-muted-foreground">{isYearly ? "/year" : "/month"}</span>
										</div>
										{isYearly && plan.discount && <p className="text-xs text-muted-foreground mt-1">Save {plan.discount}% compared to monthly</p>}
									</CardHeader>

									<CardContent className="flex-1">
										<Separator className="mb-6" />
										<ul className="space-y-3">
											{plan.features.map((feature, index) => (
												<li key={index} className="flex items-start gap-3">
													<Check className={cn("h-4 w-4 mt-0.5 flex-shrink-0", feature.included === false ? "text-muted-foreground/30" : "text-primary")} />
													<span className={cn("text-sm", feature.included === false && "text-muted-foreground line-through")}>{feature.text}</span>
												</li>
											))}
										</ul>
									</CardContent>

									<CardFooter className="pt-6">
										<Button asChild className="w-full" variant={plan.button.variant}>
											<a href={plan.button.url}>{plan.button.text}</a>
										</Button>
									</CardFooter>
								</Card>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export { PricingPlan };
